import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { DashboardResponse, Pour, Pour2Limit } from '../../../services/pours';
import { useNow } from '../../../utils/useNow';
import { pourProgressText } from '../../controlPanel/pourCardModel';
import { linkedSiteWeather } from '../../../utils/siteConditions';
import { SiteWeatherCard } from '../SiteWeather';
import { LocationCards, type LocationCardsContext } from './LocationCards';
import { pourTargetCounts } from './mobileDashboardModel';
import { LocationCardSkeleton } from './MobileLocationCard';
import { PourStatusStrip, PourStatusStripSkeleton, stripFromListPour } from './PourStatusStrip';
import { useDashboardModel } from './useDashboardModel';

export interface MobileOverviewProps {
  /** The dashboard reply; null while the first load runs. */
  data: DashboardResponse | null;
  /** The Control Panel row the pour was opened from: the strip paints from
   *  it while the first load runs. */
  listPour?: Pour | null;
  pourLimits: ReadonlyArray<Pour2Limit>;
  /** The time of the data (the last successful fetch). */
  refNow: number;
  onSelectLocation: (locationId: string) => void;
}

/**
 * The phone dashboard's Overview tab: the pour status card (status, newest
 * reading, the problem counts, how many locations, and per target how many
 * locations reached it), then every location as a card, one section per
 * monitoring function in desktop order. No pour-wide reading is picked from
 * one location: every number belongs to the card it is on. While the first
 * load runs, the status paints from the Control Panel row and the cards are
 * skeletons. No chart is loaded here.
 */
export const MobileOverview: React.FC<MobileOverviewProps> = ({
  data,
  listPour,
  pourLimits,
  refNow,
  onSelectLocation,
}) => {
  const { t } = useTranslation();
  const now = useNow();
  const model = useDashboardModel(data, pourLimits, refNow);
  const targetCounts = useMemo(
    () => (model && model.flags.maturity ? pourTargetCounts(model.locations, model.pour) : []),
    [model],
  );

  // Loading: the status from the Control Panel row (if any), card skeletons.
  if (!model || !data) {
    const fromList = listPour ? stripFromListPour(listPour) : null;
    return (
      <Box data-testid="mobile-overview" aria-busy="true">
        {fromList && listPour ? (
          <PourStatusStrip {...fromList} refNow={refNow} fromList progress={pourProgressText(listPour, now, t)} />
        ) : (
          <PourStatusStripSkeleton />
        )}
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{ mb: 1.5 }}>
            <LocationCardSkeleton />
          </Box>
        ))}
      </Box>
    );
  }

  const ctx: LocationCardsContext = {
    pour: model.pour,
    meta: model.meta,
    strengthUnit: model.strengthUnit,
    maturityFunction: model.flags.maturity,
    evaporationFunction: model.flags.evaporation,
    healthById: model.healthById,
    refNow,
    now,
    onSelect: onSelectLocation,
  };
  const site = linkedSiteWeather(data.site_weather);

  return (
    <Box data-testid="mobile-overview">
      <PourStatusStrip
        phase={model.phase.phase}
        clock={model.phase.clock}
        startsAt={model.phase.startsAt}
        endedAt={model.phase.endedAt}
        newestReadingAt={model.phase.newestReadingAt}
        counts={model.counts}
        refNow={refNow}
        timezoneIana={model.pour.timezone_iana}
        meta={model.meta}
        locationCount={model.locations.length}
        evaporationOver={Array.from(model.healthById.values()).filter((h) => h.evaporationOver).length}
        targetCounts={targetCounts}
        progress={pourProgressText(
          {
            PourID: model.pour.id,
            PourName: model.pour.name,
            Started: model.pour.started_at,
            ConcreteStart: model.pour.concrete_start,
            LoggerStart: model.pour.logger_start,
            StopType: model.pour.stop_type ?? undefined,
            XHours: model.pour.x_hours ?? undefined,
            PreSetStopTime: model.pour.preset_stop_time ?? undefined,
          } as Pour,
          now,
          t,
        )}
      />
      {/* The pour's site weather (its weather station), once, never a card
          among the locations. A pour with only site weather shows it alone.
          Only a linked station shows; no history is fetched here. */}
      {site && (
        <SiteWeatherCard
          site={site}
          pourStatus={model.pour.status}
          windSpeedUnit={model.pour.wind_speed_unit}
          timezoneIana={model.pour.timezone_iana}
          now={now}
          compact
        />
      )}
      {(model.locations.length > 0 || !site) && (
        <LocationCards locations={model.locations} ctx={ctx} />
      )}
    </Box>
  );
};

export default MobileOverview;
